import { getSelectStyles, getActionSelectStyles } from '../utility/selectStyles';
import React, { useState, useEffect } from 'react';
import { FaPlus, FaSyncAlt, FaTrash } from 'react-icons/fa';
import InfoBanner from '../components/InfoBanner';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../store/store';
import { fetchLoadBalancers, LoadBalancer } from '../store/slices/loadBalancersSlice';
import { fetchInstances } from '../store/slices/instancesSlice';
import { fetchSecurityGroups } from '../store/slices/securityGroupsSlice';
import { fetchPrivateNetwork } from '../store/slices/privateNetworkSlice';
import Select from 'react-select';
import axiosInstance from "../utility/axiosInstance";
import { useToast } from "../components/ToastContext";
import { useConfirm } from "../components/ConfirmContext";
import CreateLoadBalancerModal from '../components/CreateLoadBalancerModal';
import LoadBalancerDetailsModal from '../components/LoadBalancerDetailsModal';
import TrialLock from '../components/TrialLock';
import useAutoOpenCreateModal from '../hooks/useAutoOpenCreateModal';
import '../styles/InstancesPage.scss'; // Reuse instances styles

const ITEMS_PER_PAGE = 10;

const LoadBalancersPage: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const { showToast } = useToast();
    const { confirm } = useConfirm();

    const loadBalancers = useSelector((state: RootState) => state.loadBalancers.data);
    const status = useSelector((state: RootState) => state.loadBalancers.status);

    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [selectedLB, setSelectedLB] = useState<LoadBalancer | null>(null);
    const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
    const [deletingLBs, setDeletingLBs] = useState<Set<string>>(new Set());
    const [currentPage, setCurrentPage] = useState(0);

    const creationLocked = useAutoOpenCreateModal(setIsCreateModalOpen);

    useEffect(() => {
        dispatch(fetchLoadBalancers());
    }, [dispatch]);

    useEffect(() => {
        setCurrentPage(0);
    }, [loadBalancers.length]);

    const handleDeleteLB = async (lbId: string) => {
        const proceed = await confirm({
            title: 'Delete Load Balancer',
            message: 'Are you sure you want to delete this load balancer? This will also delete all associated listeners and pools.',
            confirmText: 'Delete',
            cancelText: 'Cancel',
            type: 'danger',
        });
        if (!proceed) return;

        setDeletingLBs(prev => new Set(prev).add(lbId));
        try {
            await axiosInstance.delete(`/v1/loadbalancer/${lbId}`);
            showToast('Load balancer deleted', 'success');
        } catch (error) {
            console.error("Error deleting LB:", error);
            showToast('Failed to delete load balancer', 'error');
        } finally {
            setDeletingLBs(prev => {
                const newSet = new Set(prev);
                newSet.delete(lbId);
                return newSet;
            });
        }
    };

    const handleLBClick = (lb: LoadBalancer) => {
        setSelectedLB(lb);
        setIsDetailsModalOpen(true);
    };

    const handleRefresh = () => {
        dispatch(fetchLoadBalancers());
    };

    const isLoading = status === 'loading';

    const totalLoadBalancers = loadBalancers.length;
    const totalPages = Math.ceil(totalLoadBalancers / ITEMS_PER_PAGE);
    const paginatedLoadBalancers = loadBalancers.slice(
        currentPage * ITEMS_PER_PAGE,
        (currentPage + 1) * ITEMS_PER_PAGE
    );

    return (
        <div className="keypairs-page">
            <div className="page-header">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <p className="page-subtitle">
                        Manage load balancers
                    </p>
                    <button 
                        className="refresh-button" 
                        onClick={handleRefresh} 
                        disabled={isLoading}
                        style={{
                            padding: '8px',
                            borderRadius: '4px',
                            border: '1px solid #e2e8f0',
                            background: 'var(--bg-card)',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '5px'
                        }}
                    >
                        <FaSyncAlt className={isLoading ? 'spin' : ''} />
                        Refresh
                    </button>
                </div>
                <InfoBanner id="load-balancers-info">
                    <span>Distribute incoming traffic across multiple instances to ensure your application stays online, even if one server fails. Our integrated load balancing handles scaling and failover automatically, providing a faster and more reliable experience for your users.</span>
                </InfoBanner>
            </div>

            <TrialLock>
                <button
                    className="create-button"
                    onClick={() => setIsCreateModalOpen(true)}
                    title="Create New Load Balancer"
                >
                    <FaPlus/>
                    <span>Create Load Balancer</span>
                </button>
            </TrialLock>

            <div className="desktop-view">
                <div className="table-container">
                    <table className="keypairs-table">
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Status</th>
                                <th>VIP Address</th>
                                <th>Provisioning</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {isLoading && loadBalancers.length === 0 ? (
                                <tr>
                                    <td colSpan={5} className="loading-state">
                                        <FaSyncAlt className="spin"/> Loading load balancers...
                                    </td>
                                </tr>
                            ) : paginatedLoadBalancers.length > 0 ? (
                                paginatedLoadBalancers.map((lb) => {
                                    const isManual = lb.tags?.includes('manual');
                                    return (
                                        <tr key={lb.id} onClick={() => handleLBClick(lb)} className="clickable-row">
                                            <td>{lb.name}</td>
                                            <td>
                                                <span className={`status-badge ${lb.operating_status.toLowerCase()}`}>
                                                    {lb.operating_status}
                                                </span>
                                            </td>
                                            <td>{lb.vip_address}</td>
                                            <td>
                                                {lb.provisioning_status === 'PENDING_CREATE' ? (
                                                    <div>
                                                        <div className="creating-progress"></div>
                                                        <div className="status-label" style={{ color: '#228b22' }}>CREATING</div>
                                                    </div>
                                                ) : lb.provisioning_status === 'PENDING_UPDATE' ? (
                                                    <div>
                                                        <div className="building-progress"></div>
                                                        <div className="status-label" style={{ color: 'var(--status-info)' }}>UPDATING</div>
                                                    </div>
                                                ) : lb.provisioning_status === 'PENDING_DELETE' || deletingLBs.has(lb.id) ? (
                                                    <div>
                                                        <div className="deleting-progress"></div>
                                                        <div className="status-label" style={{ color: '#ef4444' }}>DELETING</div>
                                                    </div>
                                                ) : (
                                                    lb.provisioning_status
                                                )}
                                            </td>
                                            <td>
                                                <div onClick={(e) => e.stopPropagation()} title={isManual ? "Actions" : "Cluster-managed load balancers cannot be deleted manually"}>
                                                    <Select
                                                        options={[
                                                            {value: 'delete', label: 'Delete Load Balancer'}
                                                        ]}
                                                        onChange={(option) => {
                                                            if (option?.value === 'delete') {
                                                                handleDeleteLB(lb.id);
                                                            }
                                                        }}
                                                        placeholder="Actions"
                                                        isSearchable={false}
                                                        isDisabled={deletingLBs.has(lb.id) || !isManual}
                                                        menuPortalTarget={document.body}
                                                        styles={getActionSelectStyles()}
                                                    />
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })
                            ) : (
                                <tr>
                                    <td colSpan={5} className="empty-state">
                                    No load balancers yet
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                    {totalPages > 1 && (
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', padding: '12px', borderTop: '1px solid var(--border)' }}>
                            <button
                                onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
                                disabled={currentPage === 0}
                                style={{ padding: '5px 14px', background: 'none', border: '1px solid var(--border)', borderRadius: '6px', cursor: currentPage === 0 ? 'not-allowed' : 'pointer', opacity: currentPage === 0 ? 0.4 : 1, color: 'var(--text-primary)' }}
                            >← Prev</button>
                            <span style={{ lineHeight: '30px', color: 'var(--text-muted)', fontSize: '0.8rem' }}>
                                {currentPage * ITEMS_PER_PAGE + 1}-{Math.min((currentPage + 1) * ITEMS_PER_PAGE, totalLoadBalancers)} of {totalLoadBalancers}
                            </span>
                            <button
                                onClick={() => setCurrentPage(p => Math.min(totalPages - 1, p + 1))}
                                disabled={currentPage >= totalPages - 1}
                                style={{ padding: '5px 14px', background: 'none', border: '1px solid var(--border)', borderRadius: '6px', cursor: currentPage >= totalPages - 1 ? 'not-allowed' : 'pointer', opacity: currentPage >= totalPages - 1 ? 0.4 : 1, color: 'var(--text-primary)' }}
                            >Next →</button>
                        </div>
                    )}
                </div>
            </div>

            <CreateLoadBalancerModal
                isOpen={isCreateModalOpen && !creationLocked}
                onClose={() => setIsCreateModalOpen(false)}
                onCreated={() => {
                    dispatch(fetchLoadBalancers());
                    // Refresh instances, SGs and networks as well because creation might take a while and user might want to check them
                    dispatch(fetchInstances());
                    dispatch(fetchSecurityGroups());
                    dispatch(fetchPrivateNetwork());
                    //setIsCreateModalOpen(false);
                }}
            />

            {selectedLB && (
                <LoadBalancerDetailsModal
                    isOpen={isDetailsModalOpen}
                    onClose={() => {
                        setIsDetailsModalOpen(false);
                        setSelectedLB(null);
                    }}
                    lbId={selectedLB.id}
                />
            )}
        </div>
    );
};

export default LoadBalancersPage;
